// ============================================================================
// Roof Designer · ImportCard — Gebäude-Dachfläche als klare Import-Karte
// ----------------------------------------------------------------------------
// Vorher: unscheinbare Inline-Zeile. Jetzt: Karte mit Auswahl, Kennwerten der
// gewählten Fläche (Name, Fläche, Neigung, Azimut, Dachtyp), Quelle-Badge und
// deutlichem Import-Button. Nach dem Import: prägnanter "aus Gebäude
// importiert"-Zustand inkl. Hinweis auf vereinfachte Geometrie/Satellit.
// Keine Logik-Änderung: Import läuft weiter über designerStateFromRoofSurface.
// ============================================================================
import type { RoofSurfaceRecord } from "../import-surface.js";
import type { DesignerState } from "../types.js";

interface Props {
  surfaces: RoofSurfaceRecord[];
  selectedId: string;
  onSelect: (id: string) => void;
  onImport: () => void;
  importSource: DesignerState["importSource"];
  satelliteActive: boolean;
}

const ROOF_TYPE_LABELS: Record<string, string> = {
  gable: "Satteldach",
  hipped: "Walmdach",
  flat: "Flachdach",
  shed: "Pultdach",
  mansard: "Mansarddach",
  pyramid: "Zeltdach",
};

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <span className="rd-import__chip">
      <span className="rd-import__chip-label">{label}</span>
      <span className="rd-import__chip-value">{value}</span>
    </span>
  );
}

export function ImportCard({ surfaces, selectedId, onSelect, onImport, importSource, satelliteActive }: Props) {
  const selected = surfaces.find((s) => s.id === selectedId) ?? null;

  return (
    <div className="rd-import" data-testid="rd-import-card">
      <div className="rd-import__head">
        <span className="rd-import__title">🏠 Dachfläche aus Gebäude übernehmen</span>
        <span className="rd-import__badge">Quelle: Gebäude-Dacherfassung</span>
      </div>

      {surfaces.length === 0 && importSource == null ? (
        <p className="rd-import__empty">
          Keine Gebäude-Dachflächen erfasst — im Tab „Gebäude" das Dach einzeichnen,
          dann erscheint es hier zur Übernahme (inkl. Satelliten-Hintergrund).
        </p>
      ) : (
        <>
          {surfaces.length > 0 && (
            <div className="rd-import__row">
              <select
                data-testid="surface-import-select"
                className="rd-select rd-import__select"
                value={selectedId}
                onChange={(e) => onSelect(e.target.value)}
              >
                <option value="">— Dachfläche wählen —</option>
                {surfaces.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label} · {ROOF_TYPE_LABELS[s.roofType] ?? s.roofType} · {Math.round(s.areaM2)} m²
                  </option>
                ))}
              </select>

              {selected && (
                <div className="rd-import__chips">
                  <Chip label="Fläche" value={`${Math.round(selected.areaM2)} m²`} />
                  <Chip label="Neigung" value={`${Math.round(selected.tiltDeg)}°`} />
                  <Chip label="Azimut" value={`${Math.round(selected.azimuthDeg)}°`} />
                  <Chip label="Dachtyp" value={ROOF_TYPE_LABELS[selected.roofType] ?? selected.roofType} />
                </div>
              )}

              <button
                data-testid="surface-import-btn"
                className="rd-import__btn"
                onClick={onImport}
                disabled={selectedId === ""}
              >
                ⤓ Dachfläche übernehmen
              </button>
            </div>
          )}

          {importSource != null && (
            <div className="rd-import__state" data-testid="surface-source-badge">
              <span className="rd-import__state-badge">✓ aus Gebäude importiert</span>
              <span className="rd-import__state-text">
                „{importSource.label}" · {Math.round(importSource.importedAreaM2)} m² ·
                Neigung {Math.round(importSource.importedTiltDeg)}° ·
                Azimut {Math.round(importSource.importedAzimuthDeg)}°
                {importSource.geometrySimplified
                  ? " · Geometrie vereinfacht aus Flächendaten (kein Geo-Polygon)"
                  : satelliteActive
                    ? " · 🛰 Satellit lagerichtig hinterlegt"
                    : ""}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
